import { StepModel } from '../../../../models/form/step';
import * as yup from 'yup';
import moment from 'moment';
import type { AnySchema } from 'yup';
import { FieldModel } from '../../../../models/form/field';
import { FieldType } from '../../../../models/form/fieldType';
import { BuildedSchema, BuildedValues, DataFieldModel, DataFieldModels, DataValueModel } from './FormModels';
import { find } from 'lodash';
import _ from 'lodash';
import { createIntl, createIntlCache } from 'react-intl';
import { getDefaultCulture } from '../../../user/language/utilities/languageUtilities';
import translations from '../../../../i18n/index';
import { getLanguage } from '../../../../utilities/LocalStorage';
import { getText, isCultureArray } from '../../../../utilities/CultureUtilities';

const initialValuesType = {
  [FieldType.Text]: '',
  [FieldType.LongText]: '',
  [FieldType.StaticText]: undefined,
  [FieldType.Date]: '',
  [FieldType.Datetime]: '',
  [FieldType.Time]: '',
  [FieldType.Birthdate]: '',
  [FieldType.Numeric]: '',
  [FieldType.CheckBox]: false,
  [FieldType.Toggle]: false,
  [FieldType.EmailAddress]: '',
  [FieldType.WebLink]: '',
  [FieldType.Select]: '',
  [FieldType.OptionList]: [],
  [FieldType.Rating]: '',
  [FieldType.Acknowledgment]: false,
  [FieldType.YesNo]: undefined,
  [FieldType.HTML]: '',
  [FieldType.Signature]: '',
  [FieldType.DocumentViewer]: '',
  [FieldType.EmbeddedPhoto]: '',
  [FieldType.Video]: '',
  [FieldType.UploadFile]: '',
  [FieldType.PaymentChoiceList]: [],
  [FieldType.PaymentOptionList]: [],
  [FieldType.Products]: [],
};

const culture = getLanguage() ?? getDefaultCulture();
const intlCache = createIntlCache();
const intl = createIntl({ locale: culture, messages: translations[culture] }, intlCache);

export const getFieldSchema = (field: FieldModel, fieldProperties: any): AnySchema => {
  let definition;

  switch (field.type) {
    case FieldType.Text:
      definition = yup.string();
      if (field.minLength) {
        definition = definition.min(field.minLength);
      }
      if (field.maxLength) {
        definition = definition.max(field.maxLength);
      }
      break;
    case FieldType.Birthdate:
      definition = yup
        .string()
        .test('is-date-valid', intl.formatMessage({ id: 'fields.fieldErrors.date' }), (value) => {
          return value || field.required ? moment(value, 'YYYY-MM-DD', true).isValid() : true;
        });
      definition = definition.test(
        'is-date-past',
        intl.formatMessage({ id: 'fields.fieldErrors.pastDate' }),
        (value) => {
          return value || field.required ? moment().diff(moment(value, 'YYYY-MM-DD', true)) > 0 : true;
        },
      );
      break;
    case FieldType.Date:
      definition = yup
        .string()
        .test('is-date-valid', intl.formatMessage({ id: 'fields.fieldErrors.date' }), (value) => {
          return value || field.required ? moment(value, 'YYYY-MM-DD', true).isValid() : true;
        });
      break;
    case FieldType.Datetime:
      definition = yup
        .string()
        .test('is-date-valid', intl.formatMessage({ id: 'fields.fieldErrors.date' }), (value) => {
          return value || field.required ? moment(value, 'YYYY-MM-DD h:mm A', true).isValid() : true;
        });
      break;
    case FieldType.EmailAddress:
      definition = yup.string().email(intl.formatMessage({ id: 'fields.fieldErrors.email' }));
      break;
    case FieldType.LongText:
      definition = yup.string();
      if (field.minLength) {
        definition = definition.min(field.minLength);
      }
      if (field.maxLength) {
        definition = definition.max(field.maxLength);
      }
      break;
    case FieldType.Numeric:
      definition = yup.number();
      if (field.min) {
        definition = definition.min(field.min);
      }
      if (field.max) {
        definition = definition.max(field.max);
      }
      break;
    case FieldType.OptionList:
      definition = yup.array().of(yup.string());
      if (field.required) {
        definition = definition.min(1);
      }
      break;
    case FieldType.Select:
      definition = yup.string();
      break;
    case FieldType.StaticText:
      definition = yup.string();
      break;
    case FieldType.Time:
      definition = yup
        .string()
        .test('is-date-valid', intl.formatMessage({ id: 'fields.fieldErrors.time' }), (value) => {
          return value || field.required ? moment(value, 'h:mm A', true).isValid() : true;
        });
      break;
    case FieldType.CheckBox:
      definition = yup.bool();
      if (field.required) {
        definition = definition.oneOf([true], intl.formatMessage({ id: 'fields.fieldErrors.requireCheck' }));
      }
      break;
    case FieldType.Toggle:
      definition = yup.bool();
      if (field.required) {
        definition = definition.oneOf([true], intl.formatMessage({ id: 'fields.fieldErrors.requireCheck' }));
      }
      break;
    case FieldType.WebLink:
      definition = yup.string();
      break;
    case FieldType.Rating:
      definition = yup.string();
      break;
    case FieldType.YesNo:
      definition = yup.string();
      break;
    case FieldType.Acknowledgment:
      definition = yup.bool();
      if (field.required) {
        definition = definition.oneOf([true], intl.formatMessage({ id: 'fields.fieldErrors.requireCheck' }));
      }
      break;
    case FieldType.HTML:
      definition = yup.string();
      break;
    case FieldType.Signature:
      definition = yup.string();
      break;
    case FieldType.DocumentViewer:
      definition = yup.string();
      break;
    case FieldType.EmbeddedPhoto:
      definition = yup.mixed();
      break;
    case FieldType.Video:
      definition = yup.string();
      break;
    case FieldType.UploadFile:
      definition = yup.mixed();
      break;
    case FieldType.PaymentOptionList:
      definition = yup.array().of(yup.string());
      if (field.required) {
        definition = definition.min(1);
      }
      break;
    case FieldType.PaymentChoiceList:
      definition = yup.array().of(yup.string());
      if (field.required) {
        definition = definition.min(1);
      }
      break;
    case FieldType.Products:
      definition = yup
        .array()
        .of(
          yup.object().shape({
            id: yup.string(),
            quantity: yup.number(),
          }),
        )
        .test((values, { createError, path }) => {
          const valid = true;

          const errors: Array<yup.ValidationError> = [];
          for (const value of values || []) {
            const product = field.products.find((p) => p.value == value.id);

            const claimedProperty =
              _.get(
                fieldProperties.properties.find(
                  (p) => p.id == field.id && p.properties.productId == value.id && p.type == 'claimed',
                ),
                'properties.quantity',
              ) || 0;

            const quantity = value?.quantity || 0;

            if (product) {
              const claimed = claimedProperty ? _.parseInt(claimedProperty) : 0;

              if (
                product.limit &&
                quantity + claimed > product.limit &&
                (!product.max || quantity + claimed < product.max || quantity < product.max)
              ) {
                errors.push(
                  createError({
                    message: intl.formatMessage(
                      { id: 'fields.fieldErrors.limit' },
                      { quantityLeft: product.limit - claimed, product: getText(product.text, intl.locale) },
                    ),
                    path: `${path}.${product.value}`,
                  }),
                );
              } else if (product.min && quantity < product.min) {
                errors.push(
                  createError({
                    message: intl.formatMessage(
                      { id: 'fields.fieldErrors.minMax' },
                      { min: product.min || 0, max: product.max },
                    ),
                    path: `${path}.${product.value}`,
                  }),
                );
              } else if (product.max && quantity > product.max) {
                errors.push(
                  createError({
                    message: intl.formatMessage(
                      { id: 'fields.fieldErrors.minMax' },
                      { min: product.min || 0, max: product.max },
                    ),
                    path: `${path}.${product.value}`,
                  }),
                );
              }
            }
          }

          if (errors.length > 0) {
            return createError({ message: () => errors });
          }

          if (valid) {
            return true;
          }

          return false;
        });

      if (field.required) {
        definition = definition
          .compact((v) => !v.quantity)
          .min(1, intl.formatMessage({ id: 'fields.fieldErrors.requiredField' }));
      }

      break;
  }

  if (field.required && definition) {
    definition = definition.required(intl.formatMessage({ id: 'fields.fieldErrors.requiredField' }));
  }

  return definition;
};

export const getInitialValue = (
  field: FieldModel,
  data?: DataFieldModels,
  culture?: string | undefined,
): FieldModel['defaultValue'] => {
  const fieldData = find(data, { id: field.id });
  if (data && fieldData) {
    const parsedValue = JSON.parse(fieldData.value) as DataValueModel;
    return parsedValue.value;
  } else if (field.defaultValue) {
    if (isCultureArray(field.defaultValue)) {
      return getText(field.defaultValue, culture);
    }

    return field.defaultValue;
  } else {
    return initialValuesType[field.type];
  }
};

export const getInitialValueMultiple = (
  field: FieldModel,
  data?: DataFieldModel,
  culture?: string | undefined,
): FieldModel['defaultValue'] => {
  if (data) {
    return JSON.parse(data.value).value;
  } else if (field.defaultValue) {
    if (isCultureArray(field.defaultValue)) {
      return getText(field.defaultValue, culture);
    }

    return field.defaultValue;
  } else {
    return initialValuesType[field.type];
  }
};

const excludedTypes = [FieldType.EmbeddedPhoto, FieldType.DocumentViewer, FieldType.Video, FieldType.WebLink];
export const buildValues = (
  formSteps: StepModel[],
  data?: DataFieldModels,
  culture?: string | undefined,
): BuildedValues => {
  const initialValues = {};

  formSteps.forEach((formStep) => {
    formStep.sections.forEach((section) => {
      if (section.multiple) {
        section.sectionsFormik?.forEach((aSection) => {
          aSection.fields.forEach((field) => {
            if (!_.includes(excludedTypes, field.type)) {
              const fieldRowId = field.id.split('@', 2);
              const dataFields = _.find(data, { id: fieldRowId[0], rowId: parseInt(fieldRowId[1]) }) as DataFieldModel;
              if (dataFields) {
                initialValues[field.id] = getInitialValueMultiple(field, dataFields, culture);
              }
            }
          });
        });
      } else {
        section.fields.forEach((field) => {
          if (!_.includes(excludedTypes, field.type)) {
            initialValues[field.id] = getInitialValue(field, data, culture);
          }
        });
      }
    });
  });
  return initialValues;
};

export const buildSchema = (formSteps: StepModel[], fieldProperties: any, stepsOnOnePage?: boolean): BuildedSchema => {
  let schema = {};
  formSteps.forEach((formStep) => {
    const schemaStep = {};
    formStep.sections.forEach((section) => {
      if (section.multiple) {
        section.sectionsFormik?.forEach((aSection) => {
          aSection.fields.forEach((field) => {
            if (field.type && field.visible && !_.includes(excludedTypes, field.type)) {
              const fieldSchema = getFieldSchema(field, fieldProperties);
              if (fieldSchema) {
                if (stepsOnOnePage) {
                  schema[field.id] = fieldSchema;
                } else {
                  schemaStep[field.id] = fieldSchema;
                }
              }
            }
          });
        });
      } else {
        section.fields.forEach((field) => {
          if (field.type && field.visible && !_.includes(excludedTypes, field.type)) {
            const fieldSchema = getFieldSchema(field, fieldProperties);
            if (fieldSchema) {
              if (stepsOnOnePage) {
                schema[field.id] = fieldSchema;
              } else {
                schemaStep[field.id] = fieldSchema;
              }
            }
          }
        });
      }
    });
    if (!stepsOnOnePage) {
      schema[formStep.id] = yup.object(schemaStep);
    }
  });
  if (stepsOnOnePage) {
    schema = yup.object(schema);
  }

  return schema;
};
